import { Box, Button, Chip, Stack, Typography } from '@mui/joy';
import type { TaskEntry } from '@/types/dashboard';

const PRIORITY_COLORS: Record<string, 'danger' | 'warning' | 'primary' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

const STATUS_COLORS: Record<string, 'danger' | 'warning' | 'primary' | 'neutral'> = {
  blocked: 'danger',
  in_progress: 'primary',
  not_started: 'neutral',
};

interface TaskListItemProps {
  task: TaskEntry;
  onStatusChange?: (taskId: number, newStatus: string) => void;
  showProject?: boolean;
}

export default function TaskListItem({ task, onStatusChange, showProject = true }: TaskListItemProps) {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();

  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: task.status === 'blocked' ? 'danger.300' : 'divider',
        borderRadius: 'sm',
        bgcolor: task.status === 'blocked' ? 'danger.50' : undefined,
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography level="body-sm" fontWeight="lg" noWrap>
              {task.name}
            </Typography>
            {showProject && (
              <Typography level="body-xs" color="neutral">
                {task.project_name}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
            <Chip size="sm" variant="soft" color={PRIORITY_COLORS[task.priority] || 'neutral'}>
              {task.priority}
            </Chip>
            <Chip size="sm" variant="soft" color={STATUS_COLORS[task.status] || 'neutral'}>
              {task.status.replace('_', ' ')}
            </Chip>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            {task.shift_type_color && (
              <Chip
                size="sm"
                variant="outlined"
                sx={{
                  borderColor: task.shift_type_color,
                  color: task.shift_type_color,
                  '--Chip-minHeight': '20px',
                }}
              >
                {task.shift_type_name}
              </Chip>
            )}
            {task.due_date && (
              <Typography level="body-xs" color={isOverdue ? 'danger' : 'neutral'} fontWeight={isOverdue ? 'lg' : undefined}>
                {isOverdue ? 'Overdue: ' : 'Due: '}
                {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </Typography>
            )}
          </Stack>

          {onStatusChange && task.status !== 'blocked' && (
            <Button
              size="sm"
              variant="plain"
              onClick={() => {
                const next = task.status === 'not_started' ? 'in_progress' : 'completed';
                onStatusChange(task.id, next);
              }}
            >
              {task.status === 'not_started' ? 'Start' : 'Complete'}
            </Button>
          )}
        </Stack>

        {task.assignees.length > 0 && (
          <Typography level="body-xs" color="neutral">
            {task.assignees.map(a => `${a.first_name} ${a.last_name}`).join(', ')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
